// ========== COMPLETE API SERVICE FOR CONTROLVITALS MIGRATION ==========
import { createComparisonCache } from '../utils/comparisonCache';
const cachedComparison = createComparisonCache();
// This file contains ALL API methods needed for 100% migration compatibility

// API Base URL - always use empty string to leverage Vite proxy for /api routes
// The Vite dev server proxies /api/* requests to http://localhost:5000
export const API_BASE_URL = '';

// ========== CSRF TOKEN MANAGEMENT ==========
let csrfToken = null;

export const getCsrfToken = async () => {
  if (csrfToken) return csrfToken;
  try {
    const response = await fetch(`${API_BASE_URL}/api/csrf-token`, {
      credentials: 'include'
    });
    const data = await response.json();
    csrfToken = data.token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    return null;
  }
};

export const refreshCsrfToken = async () => {
  csrfToken = null;
  return getCsrfToken();
};

// Secure fetch wrapper for POST requests with automatic CSRF token refresh
export const secureFetch = async (url, options = {}, retried = false) => {
  const token = await getCsrfToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };
  if (token) {
    headers['X-CSRF-Token'] = token;
  }
  const sessionToken = sessionStorage.getItem('cv_session_token');
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });
  
  // If CSRF validation failed and we haven't retried yet, refresh token and retry
  if (response.status === 403 && !retried) {
    const data = await response.clone().json().catch(() => ({}));
    if (data.error && (data.error.includes('token') || data.error.includes('bezpečnostní'))) {
      await refreshCsrfToken();
      return secureFetch(url, options, true);
    }
  }
  
  return response;
};

// Open private HTML only through an authenticated request. An opaque-origin
// sandbox prevents customer-supplied report markup from running app scripts.
export const openSavedReport = async (analysisId, variant = null, existingTab = null) => {
  const tab = existingTab || window.open('', '_blank');
  if (!tab) throw new Error('Prohlížeč zablokoval okno reportu.');
  try {
    const url = `/api/lab-audits/saved-analysis/${encodeURIComponent(analysisId)}/html`
      + (variant ? `?variant=${encodeURIComponent(variant)}` : '');
    const response = await secureFetch(url);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Report není dostupný.');
    }
    const html = await response.text();
    tab.document.body.style.margin = '0';
    const frame = tab.document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-downloads');
    frame.title = 'Uložený report analýzy';
    frame.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;border:0';
    frame.srcdoc = html;
    tab.document.body.appendChild(frame);
    tab.opener = null;
  } catch (error) {
    tab.close();
    throw error;
  }
};

export const downloadSavedEvidence = async (analysisId, format) => {
  if (!['snapshot', 'digest'].includes(format)) throw new Error('Neplatný formát exportu.');
  const response = await secureFetch(
    `/api/lab-audits/saved-analyses/${encodeURIComponent(analysisId)}/snapshot?download=${format}`
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Export není dostupný.');
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = `controlvitals-${analysisId}-${format}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Initialize CSRF token on page load
if (typeof window !== 'undefined') {
  getCsrfToken();
}

// ========== BASE API FUNCTIONS ==========
const apiFetch = async (endpoint, options = {}, retried = false) => {
  const url = `${API_BASE_URL}/api${endpoint}`;
  const method = (options.method || 'GET').toUpperCase();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  const sessionToken = sessionStorage.getItem('cv_session_token');
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }
  
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const token = await getCsrfToken();
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }
  
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers
  });
  
  // If CSRF validation failed and we haven't retried yet, refresh token and retry
  if (response.status === 403 && !retried) {
    const data = await response.clone().json().catch(() => ({}));
    if (data.error && (data.error.includes('token') || data.error.includes('bezpečnostní'))) {
      await refreshCsrfToken();
      return apiFetch(endpoint, options, true);
    }
  }
  
  if (response.status === 401) {
    sessionStorage.removeItem('cv_session_token');
    const userData = localStorage.getItem('user_data');
    if (userData) {
      localStorage.removeItem('user_data');
      window.dispatchEvent(new CustomEvent('sessionExpired'));
    }
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.message || 'API request failed');
  }
  
  return response.json();
};

// ========== AUTH API ==========
export const authApi = {
  // Login
  login: async (email, password) => {
    const response = await secureFetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 403) {
        await refreshCsrfToken();
      }
      if (data.requiresVerification) {
        const error = new Error(data.error || 'Email není ověřen');
        error.requiresVerification = true;
        error.email = data.email;
        throw error;
      }
      throw new Error(data.error || 'Přihlášení selhalo');
    }
    if (data.token) {
      sessionStorage.setItem('cv_session_token', data.token);
    }
    return data;
  },

  // Register
  register: async (email, password, name) => {
    const response = await secureFetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      body: JSON.stringify({ email, password, name })
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 403) {
        await refreshCsrfToken();
      }
      throw new Error(data.error || 'Registrace selhala');
    }
    return data;
  },

  // Logout
  logout: async () => {
    sessionStorage.removeItem('cv_session_token');
    const response = await secureFetch(`${API_BASE_URL}/api/auth/logout`, {
      method: 'POST'
    });
    return response.ok;
  },

  // Verify email
  verifyEmail: async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Ověření emailu selhalo');
    }
    return data;
  },

  // Exchange a one-time login token (returned by verifyEmail) for a real
  // session via an authenticated, CSRF-protected POST. This avoids the
  // login-CSRF risk of a GET-only auto-login.
  exchangeVerifyLogin: async (loginToken) => {
    const response = await secureFetch(`${API_BASE_URL}/api/auth/exchange-verify-login`, {
      method: 'POST',
      body: JSON.stringify({ loginToken })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Přihlášení selhalo');
    }
    return data;
  },

  // Resend verification email
  resendVerification: async (email) => {
    const response = await secureFetch(`${API_BASE_URL}/api/auth/resend-verification`, {
      method: 'POST',
      body: JSON.stringify({ email })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Odeslání ověřovacího emailu selhalo');
    }
    return data;
  },

  // Get current user (session check)
  getMe: async () => {
    const headers = {};
    const sessionToken = sessionStorage.getItem('cv_session_token');
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      credentials: 'include',
      headers
    });
    if (!response.ok) {
      return null;
    }
    return await response.json();
  },

  checkSession: async () => {
    return authApi.getMe();
  },

  // Reset password request
  requestPasswordReset: async (email) => {
    return apiFetch('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  // Reset password with token
  resetPassword: async (token, newPassword) => {
    return apiFetch('/auth/reset-password/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    });
  }
};

// ========== USER API ==========
export const userApi = {
  // Get user domains
  getDomains: async () => {
    return apiFetch('/user/domains');
  },

  // Add domain (tracked origin)
  addDomain: async (domain) => {
    return apiFetch('/user/domains', {
      method: 'POST',
      body: JSON.stringify({ domain })
    });
  },

  // Bulk add domains
  bulkAddDomains: async (domains) => {
    return apiFetch('/user/domains/bulk', {
      method: 'POST',
      body: JSON.stringify({ domains })
    });
  },

  // Remove domain
  removeDomain: async (originId) => {
    return apiFetch(`/user/domains/${originId}`, {
      method: 'DELETE'
    });
  },

  // Toggle favorite
  toggleFavorite: async (originId) => {
    return apiFetch(`/user/domains/${originId}/favorite`, {
      method: 'PUT'
    });
  },

  // Get tracked origins
  getTrackedOrigins: async () => {
    return apiFetch('/user/tracked-origins');
  },

  // Get user usage stats
  getUsage: async () => {
    return apiFetch('/user/usage');
  },

  // Get user badges
  getBadges: async () => {
    return apiFetch('/user/badges');
  },

  // Get domains limits (for current tier)
  getDomainsLimits: async () => {
    return apiFetch('/user/domains-limits');
  },

  // ===== DOMAIN GROUPS =====
  
  // Get all domain groups
  getGroups: async () => {
    const response = await fetch(`${API_BASE_URL}/api/user/domain-groups`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Nepodařilo se načíst skupiny domén');
    }
    const data = await response.json();
    return data.groups || [];
  },

  // Create new group
  createGroup: async (name, description, color) => {
    return apiFetch('/user/domain-groups', {
      method: 'POST',
      body: JSON.stringify({ name, description, color })
    });
  },

  // Update group
  updateGroup: async (groupId, data) => {
    return apiFetch(`/user/domain-groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Delete group
  deleteGroup: async (groupId) => {
    return apiFetch(`/user/domain-groups/${groupId}`, {
      method: 'DELETE'
    });
  },

  // Add domain to group
  addDomainToGroup: async (groupId, originId) => {
    return apiFetch(`/user/domain-groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ originId })
    });
  },

  // Remove domain from group
  removeDomainFromGroup: async (groupId, originId) => {
    return apiFetch(`/user/domain-groups/${groupId}/members/${originId}`, {
      method: 'DELETE'
    });
  },

  // Bulk add domains to group
  bulkAddDomainsToGroup: async (groupId, domains) => {
    return apiFetch(`/user/domain-groups/${groupId}/members/bulk`, {
      method: 'POST',
      body: JSON.stringify({ domains })
    });
  },

  // Change user tier/plan
  changeTier: async (tierSlug, billingCycle = 'monthly', confirmDowngrade = false) => {
    return secureFetch(`${API_BASE_URL}/api/user/tier`, {
      method: 'PUT',
      body: JSON.stringify({ tierSlug, billingCycle, confirmDowngrade })
    }).then(res => res.json());
  },

  // Open Stripe Customer Billing Portal (manage / cancel subscription, invoices)
  openBillingPortal: async () => {
    const res = await secureFetch(`${API_BASE_URL}/api/stripe/billing-portal`, {
      method: 'POST',
      body: JSON.stringify({})
    });
    return res.json();
  },

  // Get scheduled tier change
  getScheduledTierChange: async () => {
    return apiFetch('/user/tier/scheduled');
  },

  // Cancel scheduled tier change
  cancelScheduledTierChange: async () => {
    return secureFetch(`${API_BASE_URL}/api/user/tier/scheduled`, {
      method: 'DELETE'
    }).then(res => res.json());
  },

  // Get available tiers
  getAvailableTiers: async () => {
    return apiFetch('/user/available-tiers');
  }
};

// ========== CRUX API ==========
export const cruxApi = {
  // Get CrUX data for origin/URL
  getData: async (target, formFactor = null, analysisType = 'origin') => {
    const body = { formFactor };
    if (analysisType === 'url') {
      body.url = target;
    } else {
      body.origin = target;
    }
    
    return apiFetch('/crux', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  // Get comprehensive CrUX data
  getComprehensive: async (target, analysisType = 'origin') => {
    const body = { useCache: false };
    if (analysisType === 'url') {
      body.url = target;
    } else {
      body.origin = target;
    }
    
    return apiFetch('/crux/comprehensive', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  // Get CrUX history
  getHistory: async (origin, formFactor = null, forceRefresh = false, url = null) => {
    return apiFetch('/crux/history', {
      method: 'POST',
      body: JSON.stringify({ origin, formFactor, forceRefresh, url })
    });
  },

  // Get CrUX data for multiple origins
  getMultiple: async (origins, formFactor = null) => {
    return apiFetch('/crux/multiple', {
      method: 'POST',
      body: JSON.stringify({ origins, formFactor })
    });
  }
};

// ========== PAGESPEED INSIGHTS API ==========
export const psiApi = {
  // Analyze URL with PageSpeed Insights
  analyze: async (url, strategy = 'mobile', categories = ['performance', 'accessibility', 'best-practices', 'seo'], forceRefresh = false) => {
    return apiFetch('/pagespeed', {
      method: 'POST',
      body: JSON.stringify({ url, strategy, categories, forceRefresh })
    });
  },

  // Batch analyze multiple URLs
  batchAnalyze: async (urls, strategy = 'mobile') => {
    return apiFetch('/batch-analyze', {
      method: 'POST',
      body: JSON.stringify({ urls, strategy })
    });
  }
};

// ========== BIGQUERY API ==========
export const bigQueryApi = {
  // Get additional BigQuery metrics
  getAdditionalMetrics: async (origin, formFactor = null, auditSessionId = null) => {
    return apiFetch('/bigquery/additional-metrics', {
      method: 'POST',
      body: JSON.stringify({ origin, formFactor, auditSessionId })
    });
  },

  // Get BigQuery history
  getHistory: async (origin, formFactor = null, startDate = null, endDate = null, auditSessionId = null) => {
    return apiFetch('/bigquery/history', {
      method: 'POST',
      body: JSON.stringify({ origin, formFactor, startDate, endDate, auditSessionId })
    });
  }
};

// ========== ANALYTICS API ==========
export const analyticsApi = {
  // Get snapshot history (28-day sliding window daily snapshots)
  getSnapshotHistory: async (origin, days = 90) => {
    return apiFetch('/crux/snapshot-history', {
      method: 'POST',
      body: JSON.stringify({ origin, days })
    });
  },

  // Compare domains - use batch endpoint for efficiency (avoids rate limiting)
  getComparison: async (origins, formFactor = 'ALL', options = {}) => {
    const device = formFactor || 'ALL';
    const scope = sessionStorage.getItem('cv_session_token') || 'anonymous';
    return cachedComparison(JSON.stringify([scope, origins, device]), async () => {
    try {
      const response = await apiFetch('/analytics/compare', {
        method: 'POST',
        body: JSON.stringify({ origins, formFactor: device })
      });
      
      // Transform response to match expected format
      if (response.success && response.results) {
        return response.results.map(result => ({
          origin: result.origin,
          success: result.success === true,
          dataSource: result.dataSource || 'crux',
          performanceScore: result.performanceScore,
          data: result.success ? {
            success: true,
            dataSource: result.dataSource || 'crux',
            performanceScore: result.performanceScore,
            byFormFactor: {
              [device]: {
                metrics: result.metrics || {},
                formFactorFractions: result.formFactorFractions,
                navigationTypes: result.navigationTypes,
                lcpResourceType: result.lcpResourceType,
                collectionPeriod: result.collectionPeriod,
                dataSource: result.dataSource || 'crux'
              }
            }
          } : null,
          error: result.error || (result.blocked ? result.blockedReason : null)
        }));
      }
      
      return [];
    } catch (error) {
      console.error('[API] Comparison error:', error);
      throw error;
    }
    }, options);
  },

  // Get historical comparison
  getHistoricalComparison: async (origins, period, formFactor = null) => {
    return apiFetch('/compare/historical', {
      method: 'POST',
      body: JSON.stringify({ origins, period, formFactor })
    });
  },

  // Get comparison periods
  getComparisonPeriods: async (origins) => {
    return apiFetch('/compare/periods', {
      method: 'POST',
      body: JSON.stringify({ origins })
    });
  },

  // Check history availability
  checkHistoryAvailability: async (origins) => {
    return apiFetch('/history/check', {
      method: 'POST',
      body: JSON.stringify({ origins })
    });
  }
};

// ========== SYNC API ==========
export const syncApi = {
  // Sync history for single origin (requires CSRF)
  syncHistoryForOrigin: async (origin) => {
    const response = await secureFetch(`${API_BASE_URL}/api/history/sync`, {
      method: 'POST',
      body: JSON.stringify({ origin })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.message || 'Sync failed');
    }
    return response.json();
  },

  // Batch sync history for multiple origins (requires CSRF)
  batchSyncHistory: async (origins, formFactor = null) => {
    const response = await secureFetch(`${API_BASE_URL}/api/history/batch-sync`, {
      method: 'POST',
      body: JSON.stringify({ origins, formFactor })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.message || 'Batch sync failed');
    }
    return response.json();
  },

  // Estimate sync time (no CSRF needed for read-only)
  estimateSyncTime: async (origins) => {
    return apiFetch('/history/estimate-sync', {
      method: 'POST',
      body: JSON.stringify({ origins })
    });
  }
};

// Helper for binary file downloads
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  
  // Use timeout for mobile compatibility
  setTimeout(() => {
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  }, 0);
};

// ========== EXPORT API ==========
export const exportApi = {
  // Export to Excel (original signature for compatibility)
  exportExcel: async (origin, formFactor, cruxData, historyData, navigationTypes, formFactorFractions) => {
    const response = await fetch(`${API_BASE_URL}/api/export/excel`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        origin,
        formFactor,
        cruxData,
        historyData,
        navigationTypes,
        formFactorFractions
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Excel export failed (${response.status})`);
    }
    const blob = await response.blob();
    const cleanOrigin = origin.replace(/^https?:\/\//, '').replace(/[^a-zA-Z0-9.-]/g, '_');
    downloadBlob(blob, `crux-report-${cleanOrigin}.xlsx`);
    return { success: true };
  },

  // Export to Excel (new simplified signature)
  exportExcelSimple: async (data, filename) => {
    const response = await fetch(`${API_BASE_URL}/api/export/excel`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data, filename })
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    downloadBlob(blob, filename || 'export.xlsx');
    return { success: true };
  },

  // Export to CSV
  exportCSV: async (origin, formFactor, type, cruxData, historyData, navigationTypes, formFactorFractions) => {
    const response = await fetch(`${API_BASE_URL}/api/export/csv`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        origin, 
        formFactor, 
        type, 
        cruxData, 
        historyData, 
        navigationTypes, 
        formFactorFractions 
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `CSV export failed (${response.status})`);
    }
    const blob = await response.blob();
    const cleanOrigin = origin.replace(/^https?:\/\//, '').replace(/[^a-zA-Z0-9.-]/g, '_');
    downloadBlob(blob, `crux-${type}-${cleanOrigin}.csv`);
    return { success: true };
  },

  // Export to PDF
  exportPDF: async (origin, formFactor, cruxData, historyData, navigationTypes, formFactorFractions) => {
    const response = await fetch(`${API_BASE_URL}/api/export/pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        origin, 
        formFactor, 
        cruxData, 
        historyData, 
        navigationTypes, 
        formFactorFractions 
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `PDF export failed (${response.status})`);
    }
    const blob = await response.blob();
    const cleanOrigin = origin.replace(/^https?:\/\//, '').replace(/[^a-zA-Z0-9.-]/g, '_');
    downloadBlob(blob, `crux-report-${cleanOrigin}.pdf`);
    return { success: true };
  },

  // Export to Google Sheets
  exportGoogleSheets: async (origin, formFactor, cruxData, historyData, spreadsheetId = null) => {
    const response = await fetch(`${API_BASE_URL}/api/export/google-sheets`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        origin, 
        formFactor, 
        cruxData, 
        historyData,
        spreadsheetId 
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Google Sheets export failed (${response.status})`);
    }
    return response.json();
  },

  // Generate share URL
  generateShareUrl: async (origin, formFactor, data) => {
    return apiFetch('/share', {
      method: 'POST',
      body: JSON.stringify({ origin, formFactor, data })
    });
  },

  // Load shared analysis
  loadSharedAnalysis: async (token) => {
    return apiFetch(`/share/${token}`);
  }
};

// ========== ADMIN API ==========
export const adminApi = {
  // Get admin stats
  getStats: async () => {
    return apiFetch('/admin/stats');
  },

  // Get all users
  getUsers: async () => {
    return apiFetch('/admin/users');
  },

  // Update user
  updateUser: async (userId, data) => {
    return apiFetch(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Delete user
  deleteUser: async (userId) => {
    return apiFetch(`/admin/users/${userId}`, {
      method: 'DELETE'
    });
  },

  // Get all origins
  getOrigins: async () => {
    return apiFetch('/admin/origins');
  },

  // Update origin
  updateOrigin: async (originId, data) => {
    return apiFetch(`/admin/origins/${originId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Delete origin
  deleteOrigin: async (originId) => {
    return apiFetch(`/admin/origins/${originId}`, {
      method: 'DELETE'
    });
  },

  // Get pricing tiers
  getPricingTiers: async () => {
    return apiFetch('/admin/pricing-tiers');
  },

  // Update pricing tier
  updatePricingTier: async (tierId, data) => {
    return apiFetch(`/admin/pricing-tiers/${tierId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Get global settings
  getSettings: async () => {
    return apiFetch('/admin/settings');
  },

  // Update global settings
  updateSettings: async (settings) => {
    return apiFetch('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  },

  // Get data quality report
  getDataQuality: async () => {
    return apiFetch('/admin/data-quality');
  }
};

// ========== REPORTS API (Analysis History) ==========
export const reportsApi = {
  // Get user's saved reports
  getReports: async (limit = 50, offset = 0) => {
    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await fetch(`${API_BASE_URL}/api/reports?limit=${limit}&offset=${offset}`, {
      headers,
      credentials: 'include'
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Nepodařilo se načíst historii');
    }
    return response.json();
  },

  // Get single report with entries
  getReport: async (reportId) => {
    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await fetch(`${API_BASE_URL}/api/reports/${reportId}`, {
      headers,
      credentials: 'include'
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Nepodařilo se načíst report');
    }
    return response.json();
  },

  // Save analysis as report
  saveReport: async (name, entries, options = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await secureFetch(`${API_BASE_URL}/api/reports`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name,
        entries,
        description: options.description,
        analysisType: options.analysisType || 'comparison',
        formFactor: options.formFactor || 'ALL',
        rerunOfReportId: options.rerunOfReportId
      })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Nepodařilo se uložit report');
    }
    return data;
  },

  // Delete a report
  deleteReport: async (reportId) => {
    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await secureFetch(`${API_BASE_URL}/api/reports/${reportId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Nepodařilo se smazat report');
    }
    return response.json();
  },

  // Compare two reports
  compareReports: async (reportId1, reportId2) => {
    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await fetch(`${API_BASE_URL}/api/reports/compare?from=${reportId1}&to=${reportId2}`, {
      headers,
      credentials: 'include'
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Nepodařilo se porovnat reporty');
    }
    return response.json();
  },

  // Get domains from report for rerun
  getRerunData: async (reportId) => {
    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await secureFetch(`${API_BASE_URL}/api/reports/${reportId}/rerun`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Nepodařilo se připravit opakování');
    }
    return response.json();
  }
};

// ========== GENERAL API ==========
export const api = {
  // Health check
  checkHealth: async () => {
    const response = await fetch(`${API_BASE_URL}/api/health`);
    return await response.json();
  },

  // Get pricing info
  getPricing: async () => {
    const response = await fetch(`${API_BASE_URL}/api/pricing`);
    return await response.json();
  }
};

// ========== LEGACY COMPATIBILITY ==========
// Export legacy APIService object for backward compatibility
export const APIService = {
  // Health & Pricing
  checkHealth: api.checkHealth,
  getPricing: api.getPricing,

  // Auth
  login: authApi.login,
  logout: authApi.logout,
  register: authApi.register,
  verifyEmail: authApi.verifyEmail,
  resendVerification: authApi.resendVerification,
  getMe: authApi.getMe,
  checkSession: authApi.checkSession,

  // CrUX
  getCrUXData: cruxApi.getData,
  getCrUXComprehensive: cruxApi.getComprehensive,
  getCrUXHistory: cruxApi.getHistory,
  getCruxDataMultiple: cruxApi.getMultiple,

  // PageSpeed
  analyzeURL: psiApi.analyze,
  batchAnalyze: psiApi.batchAnalyze,
  
  // Health Check Comprehensive Analysis
  runHealthCheckAnalysis: async (url, options = {}) => {
    const { includeMultiPage = true, analysisType = 'origin' } = options;
    return apiFetch('/health-check-analysis', {
      method: 'POST',
      body: JSON.stringify({ url, includeMultiPage, analysisType })
    });
  },

  getHealthCheckResults: async (healthCheckId) => {
    return apiFetch(`/lab-audits/health-check/${healthCheckId}`);
  },

  getHealthCheckHistory: async () => {
    return apiFetch('/health-check-history');
  },

  // BigQuery
  getBigQueryAdditionalMetrics: bigQueryApi.getAdditionalMetrics,
  getBigQueryHistory: bigQueryApi.getHistory,
  getBigQueryMonthly: async (origin, auditSessionId = null) => {
    const response = await fetch(`${API_BASE_URL}/api/bigquery/core-vitals-monthly`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ origin, auditSessionId })
    });
    if (!response.ok) {
      throw new Error('Failed to fetch monthly data');
    }
    return response.json();
  },

  // User
  getUserDomains: userApi.getDomains,
  addUserDomain: userApi.addDomain,
  bulkAddUserDomains: userApi.bulkAddDomains,
  removeUserDomain: userApi.removeDomain,
  getUserUsage: userApi.getUsage,
  getUserBadges: userApi.getBadges,
  getTrackedOrigins: userApi.getTrackedOrigins,
  getUserDomainsLimits: userApi.getDomainsLimits,

  // Domain Groups
  getUserDomainGroups: userApi.getGroups,
  createDomainGroup: userApi.createGroup,
  updateDomainGroup: userApi.updateGroup,
  deleteDomainGroup: userApi.deleteGroup,
  addDomainToGroup: userApi.addDomainToGroup,
  removeDomainFromGroup: userApi.removeDomainFromGroup,
  bulkAddDomainsToGroup: userApi.bulkAddDomainsToGroup,

  // History - uses CrUX history endpoint with origin URL.
  // 404 from this endpoint is an expected "Google CrUX has no public data
  // for this origin" outcome (small / new sites that don't meet Google's
  // user-traffic threshold). Treat it as null so the UI can show an empty
  // state instead of an error in the console.
  getHistory: async (origin, limit = 90) => {
    const cleanOrigin = origin.startsWith('http') ? origin : `https://${origin.replace(/\/$/, '')}`;
    const response = await fetch(`${API_BASE_URL}/api/crux/history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ origin: cleanOrigin, formFactor: 'ALL', forceRefresh: false })
    });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch history');
    }
    const data = await response.json();
    return data.history || data;
  },

  // Analytics & Comparison
  getSnapshotHistory: analyticsApi.getSnapshotHistory,
  compareDomains: analyticsApi.getComparison,
  getHistoricalComparison: analyticsApi.getHistoricalComparison,
  getComparisonPeriods: analyticsApi.getComparisonPeriods,
  checkHistoryAvailability: analyticsApi.checkHistoryAvailability,

  // Sync
  syncHistoryForOrigin: syncApi.syncHistoryForOrigin,
  batchSyncHistory: syncApi.batchSyncHistory,
  estimateSyncTime: syncApi.estimateSyncTime,

  // Export
  exportExcel: exportApi.exportExcel,
  exportCSV: exportApi.exportCSV,
  exportPDF: exportApi.exportPDF,
  createShareLink: exportApi.generateShareUrl,
  loadSharedAnalysis: exportApi.loadSharedAnalysis,

  // Reports (Analysis History)
  getReports: reportsApi.getReports,
  getReport: reportsApi.getReport,
  saveReport: reportsApi.saveReport,
  deleteReport: reportsApi.deleteReport,
  compareReports: reportsApi.compareReports,
  getRerunData: reportsApi.getRerunData,

  // Admin
  ...adminApi,

  // Generic POST method for new endpoints
  post: async (endpoint, data) => {
    const token = await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return response.json();
  },

  async getNotifications() {
    const response = await apiFetch('/auth/notifications');
    return response;
  },

  async getNotificationReport(notificationId) {
    return apiFetch(`/notifications/${encodeURIComponent(notificationId)}/report`);
  },

  async markNotificationRead(notificationId) {
    const response = await apiFetch(`/auth/notifications/${notificationId}/read`, { method: 'POST' });
    return response;
  },

  async markAllNotificationsRead() {
    const response = await apiFetch('/auth/notifications/read-all', { method: 'POST' });
    return response;
  },

  async getMyAnalyses() {
    const response = await apiFetch('/auth/my-analyses');
    return response;
  },

  async getSavedAnalyses() {
    return apiFetch('/lab-audits/saved-analyses');
  },

  async getSavedAnalysis(analysisId) {
    return apiFetch(`/lab-audits/saved-analysis/${encodeURIComponent(analysisId)}`);
  },

  async deleteSavedAnalysis(analysisId) {
    return apiFetch(`/lab-audits/saved-analysis/${encodeURIComponent(analysisId)}`, { method: 'DELETE' });
  },

  async saveAnalysis(payload) {
    return apiFetch('/lab-audits/save-analysis', { method: 'POST', body: JSON.stringify(payload) });
  },

  async getSavedAnalysisSnapshot(analysisId) {
    return apiFetch(`/lab-audits/saved-analyses/${encodeURIComponent(analysisId)}/snapshot`);
  },

  async submitFeedback(feedbackData) {
    const response = await apiFetch('/feedback', {
      method: 'POST',
      body: JSON.stringify(feedbackData)
    });
    return response;
  },

  async getFeatureRequests() {
    const response = await apiFetch('/features');
    return response;
  },

  async submitFeatureRequest(data) {
    const response = await apiFetch('/features', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response;
  },

  async voteFeature(featureId) {
    const response = await apiFetch(`/features/${featureId}/vote`, {
      method: 'POST'
    });
    return response;
  }
};

// ========== DEFAULT EXPORT ==========
export default {
  api,
  authApi,
  userApi,
  cruxApi,
  psiApi,
  bigQueryApi,
  analyticsApi,
  syncApi,
  exportApi,
  adminApi,
  reportsApi,
  APIService,
  getCsrfToken,
  refreshCsrfToken,
  secureFetch
};
